import { useState, useEffect, useRef } from 'react';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Eye, 
  EyeOff,
  Calendar,
  Clock,
  ExternalLink,
  HelpCircle,
  Loader2,
  Search,
  FileText,
  Upload,
  Image as ImageIcon,
  X as XIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { blogApi } from '@/services/api';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Post Form Component
const PostForm = ({ post, onSave, onCancel, isNew = false }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    seo_title: post?.seo_title || '',
    meta_description: post?.meta_description || '',
    content: post?.content || '',
    excerpt: post?.excerpt || '',
    author: post?.author || 'GoCompareAI',
    tags: post?.tags?.join(', ') || '',
    featured_image: post?.featured_image || '',
    is_published: post?.is_published ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(isNew);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData(prev => ({
      ...prev,
      title: newTitle,
      slug: autoSlug ? generateSlug(newTitle) : prev.slug,
      seo_title: prev.seo_title || newTitle,
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a JPG, PNG, GIF, or WebP image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_BASE}/api/admin/upload/image`, {
        method: 'POST',
        body: formDataUpload,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();
      setFormData(prev => ({ ...prev, featured_image: result.url }));
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    
    setSaving(true);
    try {
      const postData = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        slug: formData.slug || generateSlug(formData.title),
        seo_title: formData.seo_title || formData.title,
      };
      await onSave(postData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={handleTitleChange}
            placeholder="Post title"
            required
            data-testid="blog-post-title-input"
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="slug">URL Slug *</Label>
            {isNew && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoSlug}
                  onChange={(e) => setAutoSlug(e.target.checked)}
                  className="rounded"
                />
                Auto-generate
              </label>
            )}
          </div>
          <Input
            id="slug"
            value={formData.slug}
            onChange={(e) => {
              setAutoSlug(false);
              setFormData(prev => ({ ...prev, slug: e.target.value }));
            }}
            placeholder="url-friendly-slug"
            required
            data-testid="blog-post-slug-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="seo_title">SEO Title</Label>
        <Input
          id="seo_title"
          value={formData.seo_title}
          onChange={(e) => setFormData(prev => ({ ...prev, seo_title: e.target.value }))}
          placeholder="SEO-optimized title (defaults to post title)"
          data-testid="blog-post-seo-title-input"
        />
        <p className="text-xs text-muted-foreground">Recommended: 50-60 characters</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="meta_description">Meta Description</Label>
        <Textarea
          id="meta_description"
          value={formData.meta_description}
          onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
          placeholder="Brief description for search engines..."
          className="h-20"
          data-testid="blog-post-meta-input"
        />
        <p className="text-xs text-muted-foreground">Recommended: 150-160 characters</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="excerpt">Excerpt</Label>
        <Textarea
          id="excerpt"
          value={formData.excerpt}
          onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
          placeholder="Short summary shown in blog listing..."
          className="h-20"
          data-testid="blog-post-excerpt-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Content (Markdown) *</Label>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
          placeholder="Write your blog post in Markdown format..."
          className="min-h-[300px] font-mono text-sm"
          data-testid="blog-post-content-input"
        />
        <p className="text-xs text-muted-foreground">
          Supports Markdown: **bold**, *italic*, ## headings, - lists, [links](url)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="author">Author</Label>
          <Input
            id="author"
            value={formData.author}
            onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
            placeholder="Author name"
            data-testid="blog-post-author-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="tags">Tags</Label>
          <Input
            id="tags"
            value={formData.tags}
            onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
            placeholder="AI, comparison, guide (comma-separated)"
            data-testid="blog-post-tags-input"
          />
        </div>
      </div>

      {/* Featured Image with Upload */}
      <div className="space-y-2">
        <Label>Featured Image</Label>
        <div className="flex flex-col gap-3">
          {/* URL Input */}
          <div className="flex gap-2">
            <Input
              value={formData.featured_image}
              onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
              placeholder="https://example.com/image.jpg or upload below"
              data-testid="blog-post-image-input"
              className="flex-1"
            />
            {formData.featured_image && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                title="Clear image"
              >
                <XIcon className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          {/* Upload Button */}
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Image
                </>
              )}
            </Button>
            <span className="text-xs text-muted-foreground">
              JPG, PNG, GIF, WebP (max 5MB)
            </span>
          </div>

          {/* Upload Error */}
          {uploadError && (
            <p className="text-xs text-destructive">{uploadError}</p>
          )}
          
          {/* Image Preview */}
          {formData.featured_image && (
            <div className="relative w-full max-w-sm">
              <img
                src={formData.featured_image}
                alt="Featured image preview"
                className="w-full h-32 object-cover rounded-md border"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="is_published"
          checked={formData.is_published}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
          data-testid="blog-post-publish-toggle"
        />
        <Label htmlFor="is_published">Publish immediately</Label>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving || !formData.title.trim()} data-testid="blog-post-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isNew ? 'Create Post' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  );
};

// Blog Post Card
const BlogPostCard = ({ post, onEdit, onDelete, onTogglePublish }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const isPublished = post.is_published ?? post.published;

  return (
    <Card className={cn('transition-all', !isPublished && 'opacity-70 border-dashed')} data-testid={`blog-post-card-${post.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge variant={isPublished ? 'default' : 'secondary'} className="text-xs">
                {isPublished ? 'Published' : 'Draft'}
              </Badge>
              {post.tags?.map(tag => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
            
            <h3 className="font-semibold text-foreground truncate">{post.title}</h3>
            
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
              {post.excerpt || post.meta_description || 'No excerpt'}
            </p>
            
            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(post.published_at || post.publishedAt)}
              </span>
              {post.reading_time && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {post.reading_time} min read
                </span>
              )}
              {post.author && (
                <span>by {post.author}</span>
              )}
              {post.views !== undefined && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {post.views} views
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onTogglePublish(post)}
              title={isPublished ? 'Unpublish' : 'Publish'}
              data-testid={`blog-post-toggle-${post.id}`}
            >
              {isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(post)}
              data-testid={`blog-post-edit-${post.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(post)}
              data-testid={`blog-post-delete-${post.id}`}
            >
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
            {isPublished && (
              <Button
                variant="ghost"
                size="icon"
                asChild
                title="View post"
              >
                <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Blog Manager Component
export const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(false);

  // Fetch posts (without content for list view)
  const fetchPosts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await blogApi.getAll(false); // Don't include content in list
      setPosts(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Fetch single post with full content for editing
  const handleEdit = async (post) => {
    setLoadingPost(true);
    try {
      // Fetch the full post with content
      const fullPost = await blogApi.get(post.id);
      setEditingPost(fullPost);
    } catch (err) {
      console.error('Failed to fetch post for editing:', err);
      // Fallback to the post we have (might not have content)
      setEditingPost(post);
    } finally {
      setLoadingPost(false);
    }
  };

  // Create post
  const handleCreate = async (postData) => {
    try {
      await blogApi.create(postData);
      setShowCreateDialog(false);
      fetchPosts();
    } catch (err) {
      console.error('Failed to create post:', err);
      throw err;
    }
  };

  // Update post
  const handleUpdate = async (postData) => {
    try {
      await blogApi.update(editingPost.id, postData);
      setEditingPost(null);
      fetchPosts();
    } catch (err) {
      console.error('Failed to update post:', err);
      throw err;
    }
  };

  // Delete post
  const handleDelete = async () => {
    if (!deletingPost) return;
    try {
      await blogApi.delete(deletingPost.id);
      setDeletingPost(null);
      fetchPosts();
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  // Toggle publish status
  const handleTogglePublish = async (post) => {
    const isPublished = post.is_published ?? post.published;
    try {
      if (isPublished) {
        await blogApi.unpublish(post.id);
      } else {
        await blogApi.publish(post.id);
      }
      fetchPosts();
    } catch (err) {
      console.error('Failed to toggle publish status:', err);
    }
  };

  // Filter posts by search
  const filteredPosts = posts.filter(post => 
    post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Sort: drafts first, then by date
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    const aPublished = a.is_published ?? a.published;
    const bPublished = b.is_published ?? b.published;
    if (aPublished !== bPublished) return aPublished ? 1 : -1;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

  const publishedCount = posts.filter(p => p.is_published ?? p.published).length;
  const draftCount = posts.length - publishedCount;

  return (
    <div className="space-y-4" data-testid="blog-manager">
      {/* Info Card */}
      <Card className="bg-muted/50 border-border/50">
        <CardContent className="flex items-start gap-3 py-4">
          <HelpCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Blog Management</p>
            <p>
              Create, edit, and publish blog posts. Posts support Markdown formatting.
              Published posts appear on the public /blog page and are indexed for SEO.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="gap-1">
            <FileText className="w-3 h-3" />
            {posts.length} total
          </Badge>
          <Badge variant="default" className="gap-1">
            <Eye className="w-3 h-3" />
            {publishedCount} published
          </Badge>
          {draftCount > 0 && (
            <Badge variant="secondary" className="gap-1">
              <EyeOff className="w-3 h-3" />
              {draftCount} drafts
            </Badge>
          )}
        </div>
        
        <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-blog-post-btn">
          <Plus className="w-4 h-4" />
          New Post
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search posts by title, excerpt, or tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="blog-search-input"
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="py-4">
            <p className="text-sm text-destructive">Failed to load blog posts: {error}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchPosts}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      {!loading && !error && (
        <div className="space-y-3">
          {sortedPosts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {searchQuery ? (
                  <p>No posts match your search.</p>
                ) : (
                  <>
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No blog posts yet.</p>
                    <Button 
                      variant="link" 
                      onClick={() => setShowCreateDialog(true)}
                      className="mt-2"
                    >
                      Create your first post
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            sortedPosts.map(post => (
              <BlogPostCard
                key={post.id}
                post={post}
                onEdit={handleEdit}
                onDelete={setDeletingPost}
                onTogglePublish={handleTogglePublish}
              />
            ))
          )}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Blog Post</DialogTitle>
            <DialogDescription>
              Write a new blog post. Posts can be saved as drafts or published immediately.
            </DialogDescription>
          </DialogHeader>
          <PostForm
            isNew
            onSave={handleCreate}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingPost || loadingPost} onOpenChange={(open) => !open && setEditingPost(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Blog Post</DialogTitle>
            <DialogDescription>
              Update the blog post content and settings.
            </DialogDescription>
          </DialogHeader>
          {loadingPost ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading post content...</span>
            </div>
          ) : editingPost ? (
            <PostForm
              post={editingPost}
              onSave={handleUpdate}
              onCancel={() => setEditingPost(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingPost} onOpenChange={(open) => !open && setDeletingPost(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blog Post?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingPost?.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
